let pdfjsLibPromise = null;
let mammothPromise = null;
let lastProgress = {
  status: 'idle',
  progress: 0
};

function getBaseUrl() {
  const base = process.env.BASE_URL || '/';
  return String(base).endsWith('/') ? String(base).slice(0, -1) : String(base);
}

function setProgress(onProgress, status, progress, extra = {}) {
  lastProgress = {
    status,
    progress,
    ...extra
  };

  if (typeof onProgress === 'function') {
    onProgress({ ...lastProgress });
  }
}

function makeReadableError(error, fallbackMessage) {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && error.trim()) {
    return new Error(error);
  }

  if (error && typeof error.message === 'string' && error.message.trim()) {
    return new Error(error.message);
  }

  return new Error(fallbackMessage);
}

function getFileExtension(fileName) {
  const value = String(fileName || '').toLowerCase();
  const index = value.lastIndexOf('.');
  return index >= 0 ? value.slice(index + 1) : '';
}

function isPdfFile(file) {
  const ext = getFileExtension(file && file.name);
  const type = String(file && file.type ? file.type : '').toLowerCase();

  return type === 'application/pdf' || ext === 'pdf';
}

function isDocxFile(file) {
  const ext = getFileExtension(file && file.name);
  const type = String(file && file.type ? file.type : '').toLowerCase();

  return (
    ext === 'docx' ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

function isTextLikeFile(file) {
  const ext = getFileExtension(file && file.name);
  const type = String(file && file.type ? file.type : '').toLowerCase();

  if (type.startsWith('text/')) {
    return true;
  }

  return [
    'txt',
    'md',
    'markdown',
    'json',
    'log',
    'rtf',
    'csv',
    'tsv'
  ].includes(ext);
}

export function isCasePrepDocumentSupported(file) {
  return isPdfFile(file) || isDocxFile(file) || isTextLikeFile(file);
}

export function getCasePrepDocumentKind(file) {
  if (isPdfFile(file)) {
    return 'pdf';
  }

  if (isDocxFile(file)) {
    return 'docx';
  }

  if (isTextLikeFile(file)) {
    return 'text';
  }

  return 'unsupported';
}

async function ensureAssetReachable(url, label) {
  let response = null;

  try {
    response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      credentials: 'same-origin'
    });
  } catch (error) {
    throw new Error(`Could not reach ${label} at ${url}.`);
  }

  if (!response || !response.ok) {
    throw new Error(
      `${label} was not reachable at ${url} (HTTP ${response ? response.status : 'unknown'}).`
    );
  }
}

async function loadPdfJs(onProgress) {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = (async () => {
      const base = `${getBaseUrl()}/pdfjs`;
const workerSrc = `${base}/pdf.worker.min.js`;

      setProgress(onProgress, 'Loading PDF reader...', 0.05);
      await ensureAssetReachable(workerSrc, 'PDF worker');

const mod = await import('pdfjs-dist/legacy/build/pdf.js');
      const pdfjsLib = mod && mod.default ? mod.default : mod;

      if (!pdfjsLib || typeof pdfjsLib.getDocument !== 'function') {
        throw new Error('PDF.js did not load correctly.');
      }

      if (pdfjsLib.GlobalWorkerOptions) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
      }

      return pdfjsLib;
    })().catch((error) => {
      pdfjsLibPromise = null;
      throw makeReadableError(error, 'Could not start the local PDF reader.');
    });
  }

  return pdfjsLibPromise;
}

async function loadMammoth(onProgress) {
  if (!mammothPromise) {
    mammothPromise = (async () => {
      setProgress(onProgress, 'Loading DOCX reader...', 0.05);

      const mod = await import('mammoth');
      const mammoth = mod && mod.default ? mod.default : mod;

      if (!mammoth || typeof mammoth.extractRawText !== 'function') {
        throw new Error('Mammoth did not load correctly.');
      }

      return mammoth;
    })().catch((error) => {
      mammothPromise = null;
      throw makeReadableError(error, 'Could not start the local DOCX reader.');
    });
  }

  return mammothPromise;
}

export async function warmupCasePrepDocumentReaders(onProgress) {
  setProgress(onProgress, 'Preparing local document readers...', 0.01);

  await Promise.all([
    loadPdfJs(onProgress),
    loadMammoth(onProgress)
  ]);

  setProgress(onProgress, 'Local document readers ready.', 1);
}

function normalizeDocumentText(value) {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function normalizeInlineText(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractPdfLinesFromItems(items) {
  const cleaned = (Array.isArray(items) ? items : [])
    .filter((item) => item && typeof item.str === 'string' && item.str.trim())
    .map((item) => {
      const transform = Array.isArray(item.transform) ? item.transform : [];
      const x = Number(transform[4] || 0);
      const y = Number(transform[5] || 0);
      const height = Math.abs(Number(transform[3] || item.height || 0)) || 12;
      const width = Number(item.width || 0);

      return {
        text: item.str,
        x,
        y,
        width,
        height,
        hasEOL: Boolean(item.hasEOL)
      };
    })
    .sort((a, b) => {
      if (Math.abs(b.y - a.y) > 1.5) {
        return b.y - a.y;
      }

      return a.x - b.x;
    });

  const lines = [];
  let currentLine = null;

  for (let index = 0; index < cleaned.length; index += 1) {
    const item = cleaned[index];

    if (!currentLine) {
      currentLine = {
        y: item.y,
        tolerance: Math.max(2, item.height * 0.5),
        segments: [item]
      };

      if (item.hasEOL) {
        lines.push(currentLine);
        currentLine = null;
      }

      continue;
    }

    const sameLine = Math.abs(item.y - currentLine.y) <= currentLine.tolerance;

    if (!sameLine) {
      lines.push(currentLine);
      currentLine = {
        y: item.y,
        tolerance: Math.max(2, item.height * 0.5),
        segments: [item]
      };

      if (item.hasEOL) {
        lines.push(currentLine);
        currentLine = null;
      }

      continue;
    }

    currentLine.segments.push(item);

    if (item.hasEOL) {
      lines.push(currentLine);
      currentLine = null;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines
    .map((line) => {
      const segments = line.segments.slice().sort((a, b) => a.x - b.x);

      let text = '';
      let previousEndX = null;
      let previousHeight = 0;

      for (let i = 0; i < segments.length; i += 1) {
        const segment = segments[i];

        if (previousEndX !== null) {
          const gap = segment.x - previousEndX;
          const gapThreshold = Math.max(3, previousHeight * 0.35, segment.height * 0.35);

          if (gap > gapThreshold && !text.endsWith(' ')) {
            text += ' ';
          }
        }

        text += segment.text;
        previousEndX = segment.x + segment.width;
        previousHeight = segment.height;
      }

      return normalizeInlineText(text);
    })
    .filter(Boolean);
}

async function extractPdfText(file, onProgress) {
  const pdfjsLib = await loadPdfJs(onProgress);
  const arrayBuffer = await file.arrayBuffer();

  setProgress(onProgress, 'Loading PDF...', 0.1);

  const loadingTask = pdfjsLib.getDocument({
    data: arrayBuffer
  });

  let pdf = null;

  try {
    pdf = await loadingTask.promise;

    const pageTexts = [];
    const warnings = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const progress = 0.15 + (pageNumber / Math.max(pdf.numPages, 1)) * 0.75;
      setProgress(
        onProgress,
        `Reading PDF page ${pageNumber} of ${pdf.numPages}...`,
        Math.min(progress, 0.95)
      );

      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const lines = extractPdfLinesFromItems(textContent && textContent.items ? textContent.items : []);
      const pageText = normalizeDocumentText(lines.join('\n'));

      pageTexts.push({
        pageNumber,
        text: pageText,
        lines
      });

      if (typeof page.cleanup === 'function') {
        page.cleanup();
      }
    }

    const fullText = normalizeDocumentText(
      pageTexts
        .map((page) => {
          if (!pageTexts.length || pageTexts.length === 1) {
            return page.text;
          }

          return `[Page ${page.pageNumber}]\n${page.text}`;
        })
        .filter(Boolean)
        .join('\n\n')
    );

    if (fullText.replace(/\s+/g, '').length < 10) {
      warnings.push(
        'This PDF did not expose much readable text locally. It may be scanned or image-based.'
      );
    }

    setProgress(onProgress, 'PDF text extraction complete.', 1);

    return {
      kind: 'pdf',
      text: fullText,
      pageTexts,
      warnings,
      messages: [],
      metadata: {
        pageCount: pdf.numPages
      }
    };
  } catch (error) {
    throw makeReadableError(error, 'Could not read this PDF locally.');
  } finally {
    try {
      if (pdf && typeof pdf.destroy === 'function') {
        await pdf.destroy();
      } else if (loadingTask && typeof loadingTask.destroy === 'function') {
        await loadingTask.destroy();
      }
    } catch (cleanupError) {
      console.warn('[CasePrepBuddy Documents] PDF cleanup failed', cleanupError);
    }
  }
}

async function extractDocxText(file, onProgress) {
  const mammoth = await loadMammoth(onProgress);
  const arrayBuffer = await file.arrayBuffer();

  setProgress(onProgress, 'Reading DOCX...', 0.2);

  try {
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = normalizeDocumentText(result && result.value ? result.value : '');

    const messages = Array.isArray(result && result.messages) ? result.messages : [];
    const warnings = messages.map((message) => {
      if (message && typeof message.message === 'string') {
        return message.message;
      }

      return String(message || '');
    }).filter(Boolean);

    setProgress(onProgress, 'DOCX text extraction complete.', 1);

    return {
      kind: 'docx',
      text,
      pageTexts: [],
      warnings,
      messages,
      metadata: {
        paragraphCount: text ? text.split(/\n{2,}/).filter(Boolean).length : 0
      }
    };
  } catch (error) {
    throw makeReadableError(error, 'Could not read this DOCX locally.');
  }
}

async function extractPlainText(file, onProgress) {
  setProgress(onProgress, 'Reading text document...', 0.2);

  try {
    const text = normalizeDocumentText(await file.text());

    setProgress(onProgress, 'Text extraction complete.', 1);

    return {
      kind: 'text',
      text,
      pageTexts: [],
      warnings: [],
      messages: [],
      metadata: {
        characterCount: text.length
      }
    };
  } catch (error) {
    throw makeReadableError(error, 'Could not read this text document locally.');
  }
}

export async function extractCasePrepDocument(file, onProgress) {
  if (!file) {
    throw new Error('No document file was provided.');
  }

  const kind = getCasePrepDocumentKind(file);

  if (kind === 'pdf') {
    const result = await extractPdfText(file, onProgress);
    return {
      fileName: file.name,
      mimeType: file.type || '',
      ...result
    };
  }

  if (kind === 'docx') {
    const result = await extractDocxText(file, onProgress);
    return {
      fileName: file.name,
      mimeType: file.type || '',
      ...result
    };
  }

  if (kind === 'text') {
    const result = await extractPlainText(file, onProgress);
    return {
      fileName: file.name,
      mimeType: file.type || '',
      ...result
    };
  }

  throw new Error(
    'This document type is not supported yet. Try PDF, DOCX, or a text-based document.'
  );
}

export function getCasePrepDocumentProgress() {
  return { ...lastProgress };
}